import React from 'react';
import {
  Link,
  Outlet,
  useLocation,
  useNavigate
} from 'react-router-dom';

import {
  LayoutDashboard,
  ClipboardList,
  Wallet,
  Users,
  Building2,
  BadgeCheck,
  Ban,
  LogOut,
  Shield,
  Settings,
  Tag,
  Dribbble,
  Megaphone,
  Receipt,
  Store
} from 'lucide-react';

import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

const sections = [

  {
    heading: 'Overview',

    items: [
      {
        to: '/admin/platform',
        label: 'Overview',
        icon: LayoutDashboard,
        // Phase 5: super_admin's platform-wide dashboard.
        roles: ['super_admin']
      },
      {
        to: '/admin/dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        // Phase 4: this is the per-venue dashboard, admin only.
        roles: ['admin']
      },
    ],
  },

  {
    heading: 'Audit Reports',

    items: [

      {
        to: '/admin/bookings',
        label: 'Bookings',
        icon: ClipboardList,
        roles: ['admin', 'operator']
      },

      {
        to: '/admin/payments',
        label: 'Payments',
        icon: Wallet,
        roles: ['admin']
      },

      {
        to: '/admin/billing',
        label: 'Billing',
        icon: Receipt,
        roles: ['admin']
      },

      {
        to: '/admin/invoices',
        label: 'Billing',
        icon: Receipt,
        // Platform-fee invoices across all venues.
        roles: ['super_admin']
      },

      {
        to: '/admin/customers',
        label: 'Users',
        icon: Users,
        roles: ['admin', 'operator']
      },

      {
        to: '/admin/members',
        label: 'Members',
        icon: BadgeCheck,
        roles: ['admin', 'operator']
      },
    ],
  },

  {
    heading: 'Manage',

    items: [

      {
        to: '/admin/venues',
        label: 'Venues',
        icon: Building2,
        roles: ['super_admin'],
      },

      {
        to: '/admin/venue-profile',
        label: 'Venue Profile',
        icon: Store,
        // The venue's own admin describes its facilities; the super_admin
        // only creates the venue with basic details.
        roles: ['admin'],
      },

      {
        to: '/admin/sports',
        label: 'Sports',
        icon: Dribbble,
        roles: ['super_admin'],
      },

      {
        to: '/admin/users',
        label: 'Staff',
        icon: Shield,
        roles: ['super_admin', 'admin'],
      },

      {
        to: '/admin/courts',
        label: 'Courts',
        icon: Building2,
        // Phase 4: venue-level operations belong to the venue's own admin.
        roles: ['admin']
      },

      {
        to: '/admin/plans',
        label: 'Plans',
        icon: BadgeCheck,
        roles: ['admin']
      },

      {
        to: '/admin/blocks',
        label: 'Slot Blocks',
        icon: Ban,
        roles: ['admin']
      },

      {
        to: '/admin/offers',
        label: 'Offers',
        icon: Tag,
        roles: ['super_admin', 'admin']
      },

      {
        to: '/admin/feed',
        label: 'Feed',
        icon: Megaphone,
        roles: ['super_admin'],
      },

      {
        to: '/admin/settings',
        label: 'Settings',
        icon: Settings,
        roles: ['super_admin'],
      },
    ],
  },
];

export default function AdminLayout() {

  const { user, logout } = useAuth();

  const navigate = useNavigate();

  const location = useLocation();

  const handleLogout = () => {

    logout();

    navigate('/');
  };

  return (

    <div className="min-h-screen bg-obsidian text-white flex">

      {/* DESKTOP SIDEBAR */}

      <aside className="
        hidden
        lg:flex
        w-64
        flex-col
        border-r
        border-white/10
        bg-surface
        sticky
        top-0
        h-screen
      ">

        <div className="p-6 border-b border-white/10">
          <Logo />
        </div>

        <div className="
          flex-1
          overflow-y-auto
          py-6
          px-3
          space-y-8
        ">

          {sections.map((s) => {

            // FILTER ITEMS BY ROLE
            const visibleItems = s.items.filter((it) =>
              !it.roles || it.roles.includes(user?.role)
            );

            // HIDE EMPTY SECTION
            if (visibleItems.length === 0) {
              return null;
            }

            return (

              <div key={s.heading}>

                <div className="
                  px-3
                  mb-3
                  text-[10px]
                  uppercase
                  tracking-[0.25em]
                  text-white/30
                  font-bold
                ">
                  {s.heading}
                </div>

                <div className="space-y-0.5">

                  {visibleItems.map((it) => {

                    const active =
                      location.pathname === it.to;

                    return (

                      <Link
                        key={it.to}
                        to={it.to}
                        data-testid={`admin-nav-${it.label.toLowerCase()}`}

                        className={cn(
                          `
                          flex
                          items-center
                          gap-3
                          px-3
                          py-2.5
                          text-sm
                          font-medium
                          transition-colors
                          `,
                          active
                            ? `
                              bg-volt/10
                              text-volt
                              border-l-2
                              border-volt
                            `
                            : `
                              text-white/60
                              hover:text-white
                              hover:bg-white/5
                              border-l-2
                              border-transparent
                            `
                        )}
                      >

                        <it.icon size={16} />

                        {it.label}

                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* USER PROFILE */}

        <div className="p-4 border-t border-white/10">

          <div className="flex items-center gap-3 mb-3">

            <div className="
              w-9
              h-9
              bg-volt
              text-black
              flex
              items-center
              justify-center
              font-black
              uppercase
            ">
              {user?.name?.[0] || 'A'}
            </div>

            <div className="flex-1 min-w-0">

              <div
                className="text-sm font-medium truncate"
                data-testid="admin-user-name"
              >
                {user?.name}
              </div>

              <div className="
                text-[10px]
                uppercase
                tracking-[0.25em]
                text-volt
              ">
                {user?.role?.replace('_', ' ') || 'admin'}
              </div>

            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleLogout}
            data-testid="admin-logout-button"
          >
            <LogOut size={14} />
            Sign Out
          </Button>

        </div>
      </aside>

      {/* MAIN CONTENT */}

      <div className="flex-1 min-w-0 flex flex-col">

        {/* MOBILE HEADER */}

        <header className="
          lg:hidden
          sticky
          top-0
          z-30
          bg-obsidian/90
          backdrop-blur-xl
          border-b
          border-white/10
        ">

          <div className="
            container
            mx-auto
            px-4
            h-16
            flex
            items-center
            justify-between
          ">

            <Logo />

            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
            >
              <LogOut size={14} />
              Logout
            </Button>

          </div>

          {/* MOBILE NAV */}

          <nav className="flex gap-1 overflow-x-auto px-2">

            {sections
              .flatMap((s) => s.items)
              .filter((it) =>
                !it.roles || it.roles.includes(user?.role)
              )
              .map((it) => {

                const active =
                  location.pathname === it.to;

                return (

                  <Link
                    key={it.to}
                    to={it.to}

                    className={cn(
                      `
                      flex
                      items-center
                      gap-2
                      px-3
                      py-3
                      text-[10px]
                      font-bold
                      uppercase
                      tracking-wider
                      whitespace-nowrap
                      border-b-2
                      transition-colors
                      `,
                      active
                        ? 'border-volt text-volt'
                        : 'border-transparent text-white/50'
                    )}
                  >

                    <it.icon size={12} />

                    {it.label}

                  </Link>
                );
              })}
          </nav>
        </header>

        {/* PAGE CONTENT */}

        <main className="
          flex-1
          p-6
          lg:p-10
          overflow-y-auto
        ">
          <Outlet />
        </main>

      </div>
    </div>
  );
}